import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { quizAPI } from '../lib/api';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
    GraduationCap, Clock, CheckCircle2, XCircle, ArrowRight, ArrowLeft, Loader2, Send, Award, BookOpen
} from 'lucide-react';

export default function TakeQuiz() {
    const [searchParams] = useSearchParams();
    const codeFromUrl = searchParams.get('code') || '';

    // Join phase
    const [phase, setPhase] = useState('join'); // join | quiz | results
    const [quizCode, setQuizCode] = useState(codeFromUrl);
    const [studentName, setStudentName] = useState('');
    const [studentEmail, setStudentEmail] = useState('');
    const [joining, setJoining] = useState(false);

    // Quiz phase
    const [quizData, setQuizData] = useState(null);
    const [attemptId, setAttemptId] = useState(null);
    const [currentQ, setCurrentQ] = useState(0);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [timeLeft, setTimeLeft] = useState(null);
    const timerRef = useRef(null);

    // Results phase
    const [resultData, setResultData] = useState(null);

    useEffect(() => {
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, []);

    useEffect(() => {
        if (timeLeft !== null && timeLeft > 0) {
            timerRef.current = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        clearInterval(timerRef.current);
                        handleSubmit();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timerRef.current);
        }
    }, [timeLeft !== null]);

    const handleJoin = async () => {
        if (!quizCode.trim() || !studentName.trim() || !studentEmail.trim()) {
            toast.error('Please fill in all fields'); return;
        }
        setJoining(true);
        try {
            const res = await quizAPI.join(quizCode.trim(), studentName.trim(), studentEmail.trim());
            setQuizData(res.data);
            setAttemptId(res.data.attempt_id);
            // Restore existing answers if resuming
            if (res.data.existing_answers?.length) {
                const restored = {};
                res.data.existing_answers.forEach(a => { restored[a.question_id] = a.selected_answer; });
                setAnswers(restored);
            }
            setTimeLeft(res.data.time_limit_minutes * 60);
            setPhase('quiz');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to join quiz');
        } finally {
            setJoining(false);
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        if (timerRef.current) clearInterval(timerRef.current);
        try {
            const answerList = Object.entries(answers).map(([qid, ans]) => ({ question_id: qid, selected_answer: ans }));
            const res = await quizAPI.submit(attemptId, answerList);
            setResultData(res.data);
            setPhase('results');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Submit failed');
        } finally {
            setSubmitting(false);
        }
    };

    const formatTime = (secs) => {
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    const questions = quizData?.questions || [];
    const q = questions[currentQ];

    // JOIN PHASE
    if (phase === 'join') {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-md border-slate-200 shadow-lg" data-testid="quiz-join-card">
                    <CardHeader className="text-center pb-2">
                        <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-3">
                            <GraduationCap className="h-7 w-7 text-indigo-600" />
                        </div>
                        <CardTitle className="text-xl" style={{ fontFamily: 'Manrope' }}>Join Quiz</CardTitle>
                        <p className="text-sm text-slate-500">Enter the code provided by your professor</p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label>Quiz Code</Label>
                            <Input value={quizCode} onChange={e => setQuizCode(e.target.value.toUpperCase())} placeholder="Enter 6-digit code" maxLength={6}
                                className="text-center text-lg font-mono tracking-widest" data-testid="quiz-code-input" />
                        </div>
                        <div>
                            <Label>Your Name</Label>
                            <Input value={studentName} onChange={e => setStudentName(e.target.value)} placeholder="Full name" data-testid="quiz-student-name" />
                        </div>
                        <div>
                            <Label>Your Email</Label>
                            <Input type="email" value={studentEmail} onChange={e => setStudentEmail(e.target.value)} placeholder="Email address" data-testid="quiz-student-email" />
                        </div>
                        <Button onClick={handleJoin} disabled={joining} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="quiz-join-btn">
                            {joining ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Joining...</> : <><ArrowRight className="h-4 w-4 mr-2" />Start Quiz</>}
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // QUIZ PHASE
    if (phase === 'quiz' && q) {
        const answeredCount = Object.keys(answers).length;
        return (
            <div className="min-h-screen bg-slate-50">
                {/* Top bar */}
                <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-4 py-3">
                    <div className="max-w-3xl mx-auto flex items-center justify-between">
                        <div>
                            <h2 className="font-semibold text-slate-900 text-sm" style={{ fontFamily: 'Manrope' }}>{quizData.quiz_title}</h2>
                            <p className="text-xs text-slate-500">By {quizData.professor_name} &middot; {quizData.subject}</p>
                        </div>
                        <div className="flex items-center gap-4">
                            <Badge className={cn("font-mono", timeLeft < 60 ? "bg-red-100 text-red-700" : timeLeft < 300 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700")}>
                                <Clock className="h-3 w-3 mr-1" />{formatTime(timeLeft)}
                            </Badge>
                            <span className="text-xs text-slate-500">{answeredCount}/{questions.length}</span>
                        </div>
                    </div>
                    {/* Progress bar */}
                    <div className="max-w-3xl mx-auto mt-2">
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${(answeredCount / questions.length) * 100}%` }} />
                        </div>
                    </div>
                </div>

                <div className="max-w-3xl mx-auto pt-24 pb-32 px-4" data-testid="quiz-question-area">
                    <div className="mb-4 flex items-center gap-2">
                        <Badge variant="outline" className="font-mono">Q{currentQ + 1}/{questions.length}</Badge>
                    </div>
                    {q.vignette && <p className="text-sm text-slate-600 mb-4 leading-relaxed">{q.vignette}</p>}
                    <p className="text-base font-medium text-slate-900 mb-6">{q.question_stem}</p>

                    <div className="space-y-3">
                        {q.options.map(opt => {
                            const selected = answers[q.id] === opt.letter;
                            return (
                                <button key={opt.letter} onClick={() => setAnswers(prev => ({ ...prev, [q.id]: opt.letter }))}
                                    className={cn(
                                        "w-full text-left flex items-start gap-3 p-4 rounded-lg border transition-all",
                                        selected ? "bg-indigo-50 border-indigo-300 ring-2 ring-indigo-200" : "bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                                    )} data-testid={`quiz-option-${opt.letter}`}>
                                    <span className={cn("shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium border",
                                        selected ? "bg-indigo-600 text-white border-indigo-600" : "bg-slate-50 text-slate-600 border-slate-300"
                                    )}>{opt.letter}</span>
                                    <span className={cn("text-sm pt-0.5", selected ? "text-indigo-900 font-medium" : "text-slate-700")}>{opt.text}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Bottom nav */}
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3">
                    <div className="max-w-3xl mx-auto flex items-center justify-between">
                        <Button variant="outline" onClick={() => setCurrentQ(c => Math.max(0, c - 1))} disabled={currentQ === 0} data-testid="quiz-prev-btn">
                            <ArrowLeft className="h-4 w-4 mr-1" />Previous
                        </Button>
                        <div className="flex gap-1 overflow-x-auto max-w-[40vw]">
                            {questions.map((_, i) => (
                                <button key={i} onClick={() => setCurrentQ(i)}
                                    className={cn("w-7 h-7 rounded text-xs font-mono shrink-0",
                                        i === currentQ ? "bg-indigo-600 text-white" : answers[questions[i].id] ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-500"
                                    )}>{i + 1}</button>
                            ))}
                        </div>
                        {currentQ < questions.length - 1 ? (
                            <Button onClick={() => setCurrentQ(c => c + 1)} data-testid="quiz-next-btn">
                                Next<ArrowRight className="h-4 w-4 ml-1" />
                            </Button>
                        ) : (
                            <Button onClick={handleSubmit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="quiz-submit-btn">
                                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}Submit
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // RESULTS PHASE
    if (phase === 'results' && resultData) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-2xl border-slate-200 shadow-lg" data-testid="quiz-results-card">
                    <CardHeader className="text-center pb-2">
                        <div className={cn("w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3",
                            resultData.score >= 70 ? "bg-emerald-100" : "bg-red-100"
                        )}>
                            <Award className={cn("h-8 w-8", resultData.score >= 70 ? "text-emerald-600" : "text-red-600")} />
                        </div>
                        <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>
                            {resultData.score >= 70 ? 'Well Done!' : 'Quiz Complete'}
                        </CardTitle>
                        <p className="text-slate-500">{quizData?.quiz_title}</p>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="text-center p-3 rounded-lg bg-slate-50">
                                <p className="text-3xl font-bold text-slate-900">{resultData.score}%</p>
                                <p className="text-xs text-slate-500">Score</p>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-slate-50">
                                <p className="text-3xl font-bold text-emerald-600">{resultData.correct_count}</p>
                                <p className="text-xs text-slate-500">Correct</p>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-slate-50">
                                <p className="text-3xl font-bold text-red-600">{resultData.total_questions - resultData.correct_count}</p>
                                <p className="text-xs text-slate-500">Incorrect</p>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <h3 className="font-semibold text-slate-800 text-sm">Review Answers</h3>
                            {resultData.answers.map((a, i) => {
                                const question = quizData?.questions?.[i];
                                return (
                                    <div key={a.question_id} className={cn("rounded-lg border p-3",
                                        a.is_correct ? "border-emerald-200 bg-emerald-50/50" : "border-red-200 bg-red-50/50"
                                    )}>
                                        <div className="flex items-start gap-2">
                                            {a.is_correct ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />}
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm text-slate-800 font-medium">Q{i + 1}: {question?.question_stem || ''}</p>
                                                <p className="text-xs mt-1">
                                                    <span className={a.is_correct ? "text-emerald-700" : "text-red-700"}>Your answer: {a.selected_answer}</span>
                                                    {!a.is_correct && <span className="text-emerald-700 ml-2">Correct: {a.correct_answer}</span>}
                                                </p>
                                                {a.explanation && <p className="text-xs text-slate-600 mt-1">{a.explanation}</p>}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return null;
}
